import { toast } from 'react-hot-toast';
import { authentication as teamsAuthentication } from '@microsoft/teams-js';

import { API_URL } from '../services/api';

// API_URL should be accessible here, e.g., from an env variable or a config file
// For simplicity, assuming it's globally available or configured elsewhere.
// If not, you might need to pass it or import it.
const openAuthPopup = (url, title, w, h) => {
  const dualScreenLeft =
    window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop =
    window.screenTop !== undefined ? window.screenTop : window.screenY;

  const width = window.innerWidth
    ? window.innerWidth
    : document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : window.screen.width;
  const height = window.innerHeight
    ? window.innerHeight
    : document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : window.screen.height;

  const systemZoom = width / window.screen.availWidth;
  const left = (width - w) / 2 / systemZoom + dualScreenLeft;
  const top = (height - h) / 2 / systemZoom + dualScreenTop;

  const newWindow = window.open(
    url,
    title,
    `scrollbars=yes, width=${w / systemZoom}, height=${h / systemZoom}, top=${top}, left=${left}`,
  );

  if (newWindow) {
    if (window.focus) newWindow.focus();
  } else {
    toast.error('Popup was blocked. Please allow popups for this site.');
  }
  return newWindow;
};

export const authenticateApp = async (
  appName,
  accessToken,
  onAuthSuccess,
  updateAuthApps,
  isTeamsInitialized,
  options = {},
) => {
  // `accessToken` here is user_google_id for your backend
  const { retryOnAdminConsent = true } = options;
  // Optional: called once whatever happens (success, error, popup closed or
  // blocked), so a caller waiting on the sign-in never hangs.
  let doneCalled = false;
  const done = () => {
    if (doneCalled || !options.onDone) return;
    doneCalled = true;
    options.onDone();
  };

  const retryAfterAdminConsent = async () => {
    if (!retryOnAdminConsent) {
      toast.success(
        'Organization access granted. Click "Connect Teams" again to complete setup.',
        { duration: 6000 },
      );
      if (onAuthSuccess) onAuthSuccess(appName);
      done();
      return;
    }

    toast.success('Organization access granted. Finishing Teams setup...', {
      duration: 6000,
    });

    await authenticateApp(
      appName,
      accessToken,
      onAuthSuccess,
      updateAuthApps,
      isTeamsInitialized,
      // Same choices and done callback, without a second retry. The retry
      // reports done itself, so this call doesn't.
      { ...options, retryOnAdminConsent: false },
    );
    doneCalled = true;
  };

  try {
    if (isTeamsInitialized) {
      // --- TEAMS FLOW ---
      let teamsAppAuthStartUrl =
        `${window.location.origin}/teams-app-auth-start.html` +
        `?appName=${encodeURIComponent(appName)}` +
        `&accessToken=${encodeURIComponent(accessToken)}` + // This is user_google_id
        `&backendAuthAppBaseUrl=${encodeURIComponent(API_URL + '/authApp')}`; // Base URL for /authApp/{appname}

      if (options.envType) {
        teamsAppAuthStartUrl += `&envType=${encodeURIComponent(options.envType)}`;
      }
      if (options.sharepointAccess) {
        teamsAppAuthStartUrl += `&access=${encodeURIComponent(JSON.stringify(options.sharepointAccess))}`;
      }
      // Outlook: how much history the first sync reads ({ history_days }).
      if (options.mailSync) {
        teamsAppAuthStartUrl += `&access=${encodeURIComponent(JSON.stringify(options.mailSync))}`;
      }

      try {
        const resultString = await teamsAuthentication.authenticate({
          url: teamsAppAuthStartUrl,
          width: 600,
          height: 700,
          isExternal: false,
        });

        // resultString is expected to be JSON: {status: 'success', appName: '...'} or {status: 'error', ...}
        if (resultString) {
          const resultData = JSON.parse(resultString);
          if (resultData.status === 'success') {
            if (updateAuthApps) updateAuthApps(resultData.appName);
            if (onAuthSuccess) onAuthSuccess(resultData.appName);
          } else if (resultData.status === 'admin_consent_granted') {
            await retryAfterAdminConsent();
          } else {
            // status === 'error'
            toast.error(
              resultData.error ||
                `Failed to authenticate ${resultData.appName} via Teams.`,
            );
            if (updateAuthApps) updateAuthApps(resultData.appName);
            if (onAuthSuccess) onAuthSuccess(resultData.appName);
          }
        } else {
          toast.error(
            `Authentication for ${appName} via Teams completed with no result.`,
          );
        }
      } catch (error) {
        // Catch errors from teamsAuthentication.authenticate()
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        toast.error(`Teams Auth Error for ${appName}: ${errorMessage}`);
        // Optionally call updateAuthApps/onAuthSuccess
        if (updateAuthApps) updateAuthApps();
        if (onAuthSuccess) onAuthSuccess();
      }
      done();
    } else {
      // --- REVISED WEB FLOW ---

      // 1. Construct the URL for our new intermediate page.
      let webAppAuthStartUrl =
        `${window.location.origin}/web-app-auth-start.html` +
        `?appName=${encodeURIComponent(appName)}` +
        `&accessToken=${encodeURIComponent(accessToken)}` + // Pass the user's token
        `&backendAuthAppBaseUrl=${encodeURIComponent(API_URL + '/authApp')}`;

      if (options.envType) {
        webAppAuthStartUrl += `&envType=${encodeURIComponent(options.envType)}`;
      }
      if (options.sharepointAccess) {
        webAppAuthStartUrl += `&access=${encodeURIComponent(JSON.stringify(options.sharepointAccess))}`;
      }
      // Outlook: how much history the first sync reads ({ history_days }).
      if (options.mailSync) {
        webAppAuthStartUrl += `&access=${encodeURIComponent(JSON.stringify(options.mailSync))}`;
      }

      // 2. Open the popup IMMEDIATELY.
      const popup = openAuthPopup(
        webAppAuthStartUrl,
        `${appName} Authentication`,
        600,
        700,
      );
      if (!popup) {
        // The openAuthPopup function already shows a toast error.
        done();
        return;
      }

      // 3. The existing message listener is still PERFECT. It will handle two types of messages:
      //    - A 'success' message from `web-app-auth-start.html` if the app was already authenticated.
      //    - The final 'success' or 'error' message from your backend's `/oauth_callback` endpoint.
      let poller;
      const cleanup = () => {
        if (popup && !popup.closed) {
          popup.close();
        }
        if (poller) {
          clearInterval(poller);
        }
        window.removeEventListener('message', receiveMessage);
      };

      const receiveMessage = (event) => {
        const expectedOrigin = new URL(API_URL).origin;
        // Loosening origin check slightly for flexibility between pages, but be cautious in production.
        if (
          event.origin !== window.location.origin &&
          event.origin !== expectedOrigin
        ) {
          return;
        }

        if (!event.data || typeof event.data.status === 'undefined') {
          return; // Ignore invalid messages
        }

        const { status, appName: eventAppName, error } = event.data;

        // Handle both success and error messages
        if (status === 'success') {
          toast.success(`Successfully connected ${eventAppName}!`);
          if (updateAuthApps) updateAuthApps(eventAppName);
          if (onAuthSuccess) onAuthSuccess(eventAppName);
        } else if (status === 'admin_consent_granted') {
          cleanup();
          void retryAfterAdminConsent();
          return;
        } else if (status === 'error') {
          toast.error(error || `Failed to connect ${eventAppName}.`);
          if (updateAuthApps) updateAuthApps(eventAppName);
        }

        // Clean up
        cleanup();
        done();
      };

      window.addEventListener('message', receiveMessage, false);

      // Also, monitor if the user closes the popup manually
      poller = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(poller);
          // Remove the listener if the popup is closed without a message
          // to prevent it from lingering.
          window.removeEventListener('message', receiveMessage);
          done();
        }
      }, 500);
    }
  } catch (error) {
    // Catch errors from the initial callAuthAppService in web flow
    toast.error(
      error.message || 'Failed to start authentication. Please try again.',
    );
    done();
  }
};
